import React, { FC, createContext, ReactNode } from "react";
import { useLazyQuery, useMutation, ApolloError } from "@apollo/client";
import { isRecaptchaErrorCode, ResultCodes } from "@app/service/resultCodes";
import UserService from "@app/service/user";
import { Language } from "./intlWrapper";
import { getGraphQLErrorCode } from "@app/provider/error/graphQLErrorCode";
import { RecaptchaTokens } from "@app/hooks";

export interface ResetPasswordData {
  email?: string;
}

export interface ResetPasswordController {
  resetPassword: (
    email: string,
    tokens?: RecaptchaTokens
  ) => Promise<ResetPasswordData | undefined>;
  resetPasswordIsLoading: boolean;
  resetPasswordData?: ResetPasswordData | null;
  resetPasswordError?: string;
  checkResetToken: (token: string) => void;
  resetTokenData?: { email: string; language?: Language };
  resetTokenError?: string;
  setPassword: (props: { token: string; password: string }) => void;
  setPasswordIsLoading: boolean;
  setPasswordData?: { success: boolean } | null;
  setPasswordError: ApolloError | undefined;
  clearResetPasswordError: () => void;
}

export const Context = createContext<ResetPasswordController>({
  resetPassword: async () => ({}),
  checkResetToken: () => undefined,
  setPassword: async () => undefined,
  resetPasswordIsLoading: false,
  setPasswordIsLoading: false,
  setPasswordError: undefined,
  clearResetPasswordError: () => undefined,
});

Context.displayName = "ResetPasswordContext";

export const extractResetPasswordError = (
  error: ApolloError | undefined
): ResultCodes | undefined => {
  if (!error) return undefined;
  const code = getGraphQLErrorCode(error) as ResultCodes | undefined;
  return code ?? ResultCodes.UNCAUGHT_ERROR;
};

const ResetPasswordProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const [
    resetPassword,
    {
      loading: resetPasswordIsLoading,
      data: resetPasswordData,
      error: resetPasswordErrorData,
    },
  ] = useLazyQuery<{ resetPassword: ResetPasswordData }>(
    UserService.ResetPassword(),
    {
      fetchPolicy: "cache-and-network",
    }
  );

  React.useEffect(() => {
    const code = extractResetPasswordError(resetPasswordErrorData);
    setResetPasswordError(
      code && !isRecaptchaErrorCode(code) ? code : undefined
    );
  }, [resetPasswordErrorData]);

  const [resetPasswordError, setResetPasswordError] = React.useState<
    ResultCodes | undefined
  >(undefined);

  const [checkResetToken, { data: resetTokenData, error: resetTokenError }] =
    useLazyQuery<{
      checkResetToken: { email: string; language?: Language };
    }>(UserService.CheckResetToken(), { fetchPolicy: "no-cache" });

  const [
    setPassword,
    {
      loading: setPasswordIsLoading,
      data: setPasswordData,
      error: setPasswordError,
    },
  ] = useMutation<{ setPassword: { success: boolean } }>(
    UserService.SetPassword()
  );

  const handleResetPassword = async (
    email: string,
    tokens?: RecaptchaTokens
  ) => {
    const response = await resetPassword({
      variables: {
        resetPasswordData: {
          email,
          recaptchaToken: tokens?.recaptchaToken,
          recaptchaV2Token: tokens?.recaptchaV2Token,
        },
      },
    });
    if (response.error) {
      throw response.error;
    }
    return response.data?.resetPassword;
  };

  const handleCheckResetToken = (token: string) => {
    checkResetToken({ variables: { token } });
  };

  const handleSetPassword = ({
    token,
    password,
  }: {
    token: string;
    password: string;
  }) => {
    setPassword({ variables: { props: { token, password } } });
  };

  const handleClearResetPasswordError = () => {
    setResetPasswordError(undefined);
  };

  return (
    <Context.Provider
      value={{
        resetPassword: handleResetPassword,
        resetPasswordIsLoading,
        resetPasswordData: resetPasswordData?.resetPassword,
        resetPasswordError,
        checkResetToken: handleCheckResetToken,
        resetTokenData: resetTokenData?.checkResetToken,
        resetTokenError: getGraphQLErrorCode(resetTokenError),
        setPassword: handleSetPassword,
        setPasswordIsLoading,
        setPasswordData: setPasswordData?.setPassword,
        setPasswordError,
        clearResetPasswordError: handleClearResetPasswordError,
      }}
    >
      {children}
    </Context.Provider>
  );
};

export default ResetPasswordProvider;
